import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { PrintableReport } from "./PrintableReport";
import { getSignedFileUrl } from "@/lib/getSignedFileUrl";
import { formatClassLabel } from "@/lib/classLabels";

interface SessionLite {
  id: string;
  topic: string | null;
  module_code: string | null;
  class: string | null;
  session_date: string;
}

interface Row {
  id: string;
  name: string;
  last_name: string;
  reg_no: string;
  signature_url: string | null;
  status: "present" | "absent";
}

/**
 * Per-session printable attendance sheet.
 * Renders the entire active class roster (alphabetical A → Z) with a Status
 * column ("Present" for scanned students, blank for absentees) and a
 * Signature column reserved for the auto-captured signature flow.
 * Hidden on screen, triggered by window.print() — see mem://technical/print-logic.
 */
export function SessionPrintableSheet({ session }: { session: SessionLite }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [moduleName, setModuleName] = useState<string>("");
  const [officerSig, setOfficerSig] = useState<string | null>(null);
  const [officerName, setOfficerName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  async function loadRoster(): Promise<Row[]> {
    // 1. Scanned students for this session
    const { data: records, error } = await supabase
      .from("attendance_records")
      .select("id, student_id, signature_url")
      .eq("session_id", session.id)
      .eq("attendance_type", "session")
      .eq("status", "present");
    if (error) {
      console.error("Failed loading session roster:", error);
      return [];
    }
    const recs = (records ?? []) as { id: string; student_id: string; signature_url: string | null }[];
    const presentMap = new Map(recs.map((r) => [r.student_id, r]));

    // Recover class from module for legacy sessions where session.class is NULL
    let sessionClass = session.class;
    if (!sessionClass && session.module_code) {
      const { data: mod } = await supabase
        .from("modules")
        .select("class")
        .eq("module_code", session.module_code)
        .maybeSingle();
      sessionClass = mod?.class ?? null;
    }

    // 2. Full active class roster
    let classStudents: any[] = [];
    if (sessionClass) {
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, signature_url")
        .eq("class", sessionClass)
        .in("status", ["active", "registered_active", "unregistered_active"]);
      classStudents = (data ?? []) as any[];
    }

    // 3. Include scanned students that aren't in the current class roster
    const knownIds = new Set(classStudents.map((s) => s.id));
    const orphanIds = recs.map((r) => r.student_id).filter((id) => id && !knownIds.has(id));
    if (orphanIds.length > 0) {
      const { data: extra } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, signature_url")
        .in("id", orphanIds);
      classStudents = classStudents.concat((extra ?? []) as any[]);
    }

    return classStudents
      .map((s) => {
        const rec = presentMap.get(s.id);
        const name = `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim() || "—";
        return {
          id: rec?.id ?? `absent-${s.id}`,
          name,
          last_name: name.toLowerCase(),
          reg_no: s.student_number ?? "—",
          signature_url: rec?.signature_url ?? s.signature_url ?? null,
          status: (rec ? "present" : "absent") as "present" | "absent",
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }

  async function loadAll() {
    setLoading(true);
    try {
      const [roster, modRes, settingRes] = await Promise.all([
        loadRoster(),
        session.module_code
          ? supabase.from("modules").select("module_name").eq("module_code", session.module_code).maybeSingle()
          : Promise.resolve({ data: null } as any),
        supabase
          .from("system_settings")
          .select("key, value")
          .in("key", ["head_academic_signature", "academic_officer_signature_url"]),
      ]);

      setRows(roster);
      setModuleName((modRes as any)?.data?.module_name ?? "");

      const settings = ((settingRes as any).data as any[]) ?? [];
      const head = settings.find((s) => s.key === "head_academic_signature");
      const legacy = settings.find((s) => s.key === "academic_officer_signature_url");
      let sigUrl: string | null = null;
      let sigName = "";
      if (head?.value) {
        const v = head.value;
        sigName = v?.name ?? "";
        if (v?.file_path) sigUrl = await getSignedFileUrl(v.file_path);
        else if (typeof v === "string") sigUrl = v;
      }
      if (!sigUrl && legacy?.value) {
        const lv = legacy.value;
        sigUrl = typeof lv === "string" ? lv : lv?.url ?? null;
      }
      setOfficerSig(sigUrl);
      setOfficerName(sigName);
    } finally {
      setLoading(false);
    }
  }

  async function handlePrint() {
    await loadAll();
    setPrinting(true);
    // Wait for DOM, print, then unmount the printable area
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrinting(false), 500);
    }, 200);
  }

  const meta = [
    { label: "Class", value: formatClassLabel(session.class) },
    { label: "Module", value: `${session.module_code ?? "—"}${moduleName ? " — " + moduleName : ""}` },
    { label: "Session", value: session.topic || "Untitled session" },
  ];

  return (
    <>
      <Button variant="outline" size="sm" onClick={handlePrint} disabled={loading} title="Print session attendance">
        {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Printer className="w-4 h-4 mr-1" />}
        Print Session
      </Button>

      {printing && (() => {
        const ROWS_PER_PAGE = 25;
        const pages: Row[][] = rows.length === 0
          ? [[]]
          : Array.from({ length: Math.ceil(rows.length / ROWS_PER_PAGE) }, (_, i) =>
              rows.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE),
            );
        return (
        <PrintableReport title="SESSION ATTENDANCE SHEET" meta={meta}>
          {pages.map((pageRows, pageIdx) => (
            <div key={pageIdx} className={pageIdx < pages.length - 1 ? "session-page" : undefined}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "5%" }}>SN</th>
                    <th style={{ width: "35%" }}>Student Name</th>
                    <th style={{ width: "20%" }}>NACTEVET No.</th>
                    <th style={{ width: "15%" }}>Status</th>
                    <th style={{ width: "25%" }}>Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr><td colSpan={5} style={{ textAlign: "center", padding: 16, color: "#777" }}>No active students in this class.</td></tr>
                  ) : pageRows.map((r, i) => (
                    <tr key={r.id}>
                      <td>{pageIdx * ROWS_PER_PAGE + i + 1}</td>
                      <td>{r.name}</td>
                      <td style={{ fontFamily: "monospace" }}>{r.reg_no}</td>
                      <td>{r.status === "present" ? "Present" : ""}</td>
                      <td>{r.signature_url
                        ? <img src={r.signature_url} alt="sig" style={{ height: 28, maxWidth: 120, objectFit: "contain" }} />
                        : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: "8pt", color: "#777", textAlign: "right", marginTop: 6 }}>
                Page {pageIdx + 1} of {pages.length} · {rows.length} student{rows.length === 1 ? "" : "s"}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 32, paddingTop: 12, borderTop: "1px solid #999" }}>
            <div style={{ fontSize: "10pt", fontWeight: 600, marginBottom: 8 }}>Certified by Academic Officer</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 32, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Signature</div>
                {officerSig
                  ? <img src={officerSig} alt="Academic Officer signature" style={{ height: 50, maxWidth: 200, objectFit: "contain" }} />
                  : <div style={{ borderBottom: "1px solid #333", height: 50 }} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Name</div>
                <div style={{ borderBottom: "1px solid #333", height: 24, fontSize: "10pt", paddingBottom: 2 }}>
                  {officerName}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "9pt", color: "#555", marginBottom: 4 }}>Date</div>
                <div style={{ borderBottom: "1px solid #333", height: 24 }} />
              </div>
            </div>
          </div>
        </PrintableReport>
        );
      })()}
    </>
  );
}
